/**
 * This module holds two families of date helpers that must not be confused with each other.
 *
 * 1. BROWSER-LOCAL helpers (formatDateToyyyMMdd, timeStringToDate, dateToTimeString, atEndOfDay,
 *    ceilToNext15Minutes, roundToNearest15Minutes) read and write wall-clock time in the zone the
 *    browser happens to sit in. They are browser-local by construction and are called only from
 *    sites where that is the correct semantics (for example the DatePicker HH:mm round-trip, which
 *    is zone-symmetric).
 *
 * 2. RESOURCE-LOCAL helpers (zoneOffsetMsAt, zoneWallClockToUtcMs, resourceLocalDateTimeToMs,
 *    msToResourceLocalTimeString, msToResourceLocalYmd, msToResourceLocalCalendarDate,
 *    msToResourceLocalWallClockDate, resourceLocalWallClockDateToMs, resourceLocalDayBoundsMs,
 *    isForeignTimezoneAt, formatZoneOffsetLabel) take an explicit IANA
 *    timezone id as their FIRST parameter. There is deliberately no zone-less resource-local path:
 *    the zone is a required parameter so a reader can never be in doubt about which clock a
 *    conversion was made against (D-15). Every one of them falls back to browser-local when the id
 *    is null, blank or unresolvable, so no caller has to repeat that guard (D-09).
 *
 * The instant (UTC epoch-ms) is the primary value throughout; a wall clock is one of two
 * renderings of an instant, never the thing itself.
 */

/**
 * Days of week in the format used by the API (PascalCase).
 * Index 0 = All, 1 = Sunday, 2 = Monday, ..., 7 = Saturday
 */
export const DAYS_OF_WEEK = ['All', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
export const ALL_DAY_OF_WEEK = DAYS_OF_WEEK[0];

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];

/**
 * Get the day of week name for a given Date object
 */
export function getDayOfWeekFromDate(date: Date): DayOfWeek {
    return DAYS_OF_WEEK[date.getDay() + 1];
}

export function normalizeDayOfWeek(dayOfWeek: string | null | undefined): string {
    return dayOfWeek ?? ALL_DAY_OF_WEEK;
}

export function serializeDayOfWeek(dayOfWeek: string | null | undefined): string | null {
    if (dayOfWeek == null) return null;
    return dayOfWeek === ALL_DAY_OF_WEEK ? null : dayOfWeek;
}

export function dayOfWeekMatches(dayOfWeek: string | null | undefined, targetDayOfWeek: string): boolean {
    const normalizedDayOfWeek = normalizeDayOfWeek(dayOfWeek);
    return normalizedDayOfWeek === ALL_DAY_OF_WEEK || normalizedDayOfWeek === targetDayOfWeek;
}

export function expandDayOfWeek(dayOfWeek: string | null | undefined): string[] {
    const normalizedDayOfWeek = normalizeDayOfWeek(dayOfWeek);
    return normalizedDayOfWeek === ALL_DAY_OF_WEEK ? [...DAYS_OF_WEEK.slice(1)] : [normalizedDayOfWeek];
}

export function formatDateToyyyMMdd(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function parseDateTime(dateTimeStr: string): Date {
    // Replace space with 'T' and add 'Z' to treat it as UTC
    const isoStr = dateTimeStr.replace(' ', 'T') + 'Z';
    return new Date(isoStr);
}

export function atEndOfDay(date: Date | null): Date | null {
    if (!date) return null;
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay;
}

/**
 * Convert a time string "HH:mm" to a Date object (today's date with the given time).
 * Returns null if the string is empty or invalid.
 */
export function timeStringToDate(timeStr: string | null | undefined): Date | null {
    if (!timeStr || !timeStr.trim()) return null;
    const parts = timeStr.split(':').map(Number);
    if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
    const date = new Date();
    date.setHours(parts[0], parts[1], 0, 0);
    return date;
}

/**
 * Convert a Date object to a time string "HH:mm".
 * Returns empty string if date is null/undefined.
 */
export function dateToTimeString(date: Date | null | undefined): string {
    if (!date) return '';
    const h = String(date.getHours()).padStart(2, '0');
    const m = String(date.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
}

/**
 * Ceil a Date's minutes to the next 15-minute step in the future.
 * If already on a 15-min boundary, returns the same time.
 * E.g., 9:01 → 9:15, 9:15 → 9:15, 9:16 → 9:30, 9:46 → 10:00
 */
export function ceilToNext15Minutes(date: Date | null | undefined): Date | null {
    if (!date) return null;
    const ceiled = new Date(date);
    const minutes = ceiled.getMinutes();
    const remainder = minutes % 15;
    if (remainder !== 0) {
        ceiled.setMinutes(minutes + (15 - remainder), 0, 0);
    } else {
        ceiled.setSeconds(0, 0);
    }
    return ceiled;
}

/**
 * Round a Date's minutes to the nearest 15-minute step.
 * E.g., 9:07 → 9:00, 9:08 → 9:15, 9:22 → 9:15, 9:53 → 10:00
 */
export function roundToNearest15Minutes(date: Date | null | undefined): Date | null {
    if (!date) return null;
    const rounded = new Date(date);
    const minutes = rounded.getMinutes();
    const remainder = minutes % 15;
    if (remainder < 8) {
        rounded.setMinutes(minutes - remainder, 0, 0);
    } else {
        rounded.setMinutes(minutes + (15 - remainder), 0, 0);
    }
    return rounded;
}

/* ------------------------------------------------------------------------------------------------
 * RESOURCE-LOCAL CONVERSION (D-15)
 * Hand-rolled on Intl, no new dependency. See the file header for why the zone is always explicit.
 * ---------------------------------------------------------------------------------------------- */

interface ZoneWallClockParts {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
}

/**
 * Render an instant as wall-clock parts in the given zone.
 * Returns null when the id is null/blank or when Intl rejects it — the same catch-to-fallback
 * policy as localDateOf in lib/services/reservation.ts.
 */
function zoneWallClockPartsAt(timeZoneId: string | null | undefined, instantMs: number): ZoneWallClockParts | null {
    if (!timeZoneId || !timeZoneId.trim()) return null;
    if (!Number.isFinite(instantMs)) return null;
    try {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timeZoneId,
            hour12: false,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
        const parts = formatter.formatToParts(new Date(instantMs));
        const lookup: Record<string, string> = {};
        for (const part of parts) {
            lookup[part.type] = part.value;
        }
        const year = Number(lookup.year);
        const month = Number(lookup.month);
        const day = Number(lookup.day);
        // Some ICU builds emit '24' for midnight under hour12: false; normalise it to 0.
        const rawHour = Number(lookup.hour);
        const hour = rawHour === 24 ? 0 : rawHour;
        const minute = Number(lookup.minute);
        if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day) || !Number.isFinite(hour) || !Number.isFinite(minute)) return null;
        return { year, month, day, hour, minute };
    } catch {
        return null;
    }
}

/**
 * The zone's offset east of UTC, in milliseconds, at the given instant.
 * E.g. Europe/Rome in January returns 3600000, in July 7200000.
 * Returns null when the zone id is null/blank or Intl rejects it (a Windows identifier, a corrupt
 * value) — callers degrade to browser-local rather than throwing (D-09).
 */
export function zoneOffsetMsAt(timeZoneId: string | null | undefined, instantMs: number): number | null {
    const parts = zoneWallClockPartsAt(timeZoneId, instantMs);
    if (!parts) return null;
    // Careful: seconds/ms of the instant are intentionally dropped here and re-added, because the
    // reassembled UTC value must describe the same second as the source instant.
    const source = new Date(instantMs);
    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, source.getUTCSeconds(), source.getUTCMilliseconds());
    return asUtc - instantMs;
}

const HOUR_MS = 60 * 60 * 1000;

/**
 * How far either side of the requested wall clock the zone's offset is sampled when looking for a
 * DST transition.
 *
 * Probing a minute either side of an already-resolved candidate would be useless: a transition
 * moves the offset by a whole hour, so the candidate and its immediate neighbours sit on the same
 * side of it. The window must instead be wide enough to straddle the transition from the NAIVE UTC
 * value, which can be up to 14 hours away from the real instant (UTC+14 at Pacific/Kiritimati,
 * UTC-12 at Etc/GMT+12). 26 hours covers that worst case plus the transition step, while staying
 * far narrower than the roughly six months between a zone's two transitions — so at most one
 * transition falls inside the window and exactly the two offsets in play are sampled.
 */
const OFFSET_PROBE_MS = 26 * HOUR_MS;

/**
 * Every distinct instant that renders as the given wall clock in the given zone.
 *
 * Returns null when the zone cannot be resolved; an empty array when the wall clock does not exist
 * (the spring-forward gap); one entry on a normal day; two entries for the repeated hour at the
 * autumn fall-back, ordered earlier-first.
 *
 * Each candidate is produced by inverting one of the two offsets in play and is then rendered back
 * into the zone and compared with the request, so an offset that does not actually apply is
 * discarded rather than silently returning a wrong instant.
 */
function wallClockCandidates(timeZoneId: string | null | undefined, year: number, month: number, day: number, hours: number, minutes: number): number[] | null {
    const naiveUtcMs = Date.UTC(year, month - 1, day, hours, minutes, 0, 0);
    const offsetBefore = zoneOffsetMsAt(timeZoneId, naiveUtcMs - OFFSET_PROBE_MS);
    const offsetAfter = zoneOffsetMsAt(timeZoneId, naiveUtcMs + OFFSET_PROBE_MS);
    if (offsetBefore === null || offsetAfter === null) return null;

    const offsets = offsetBefore === offsetAfter ? [offsetBefore] : [offsetBefore, offsetAfter];
    const found: number[] = [];
    for (const offset of offsets) {
        const candidate = naiveUtcMs - offset;
        const rendered = zoneWallClockPartsAt(timeZoneId, candidate);
        if (!rendered) return null;
        const roundTrips = rendered.year === year && rendered.month === month && rendered.day === day && rendered.hour === hours && rendered.minute === minutes;
        if (roundTrips && !found.includes(candidate)) {
            found.push(candidate);
        }
    }
    found.sort((a, b) => a - b);
    return found;
}

/**
 * Convert a wall clock in the given zone to a UTC epoch-ms instant. `month` is 1-based.
 * Returns null when the zone cannot be resolved.
 *
 * DST policy mirrors the backend's ConvertLocalToUtcSafe (ResourceSlots/ResourceSlotService.cs) so
 * the ms the wizard computes always equals the ms the backend would compute (D-14):
 *  - spring-forward gap (the requested wall clock does not exist): shift forward one hour, which is
 *    what the backend's catch (ArgumentException) branch does;
 *  - autumn fall-back ambiguity (the repeated hour): the LATER instant, equivalently the one with
 *    the SMALLER UTC offset — see the comment inside the ambiguous branch below. Stated as an
 *    offset rule rather than as "standard time", because the two only coincide when daylight time
 *    is the larger offset, which is not true of every zone.
 */
export function zoneWallClockToUtcMs(timeZoneId: string | null | undefined, year: number, month: number, day: number, hours: number, minutes: number): number | null {
    const candidates = wallClockCandidates(timeZoneId, year, month, day, hours, minutes);
    if (candidates === null) return null;

    if (candidates.length > 0) {
        // D-14 AMBIGUOUS BRANCH — deliberately pinned to the backend.
        //
        // When `candidates` holds two entries the requested wall clock is the repeated hour at the
        // autumn fall-back (for example 02:30 on 2026-10-25 in Europe/Rome, which occurs once at
        // 00:30Z on daylight time and again at 01:30Z on standard time). Math.max selects the LATER
        // instant, i.e. standard time.
        //
        // THE RULE IS AN OFFSET CHOICE, NOT "whatever .NET picks": the LATER UTC instant is by
        // definition the one with the SMALLER UTC offset, because UTC = wall - offset. State it
        // that way at both ends. ConvertLocalToUtcSafe now selects
        // GetAmbiguousTimeOffsets(...).Min() explicitly rather than relying on
        // TimeZoneInfo.ConvertTimeToUtc's "assume standard time" default — that default is only
        // equivalent when daylight time is the LARGER offset, and for a zone modelled with negative
        // DST (Europe/Dublin, Africa/Casablanca as .NET reads IANA TZif data) standard time is the
        // EARLIER instant, which would silently invert against this Math.max.
        //
        // A paired probe on this host confirmed both ends produce 1792891800000
        // (2026-10-25T01:30Z) for that wall clock, and 1774747800000 (2026-03-29T01:30Z) for the
        // spring-forward case below.
        //
        // NOTE: D-14's literal wording says the ambiguous time "takes the earlier instant". This
        // implementation deviates from that wording on purpose and takes the later one, because
        // D-14's stated PURPOSE is that the wizard and the backend never disagree, and the backend
        // already picks the later instant for slot spans that are persisted today. Honouring the
        // wording would have meant changing ExpandSlotToSpans' output for already-stored
        // availability and maintenance windows.
        //
        // The two ends are pinned to each other: if you change this rule, change
        // ResourceSlotService.ConvertLocalToUtcSafe in the backend in the same commit, and vice
        // versa. A one-sided change makes the preview and the saved span differ for that hour.
        return Math.max(...candidates);
    }

    // The requested wall clock does not exist in this zone: it fell in the spring-forward gap.
    // Shift the REQUEST forward by one hour and resolve again, exactly as the backend's
    // catch (ArgumentException) branch does.
    const shifted = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0) + HOUR_MS);
    const shiftedCandidates = wallClockCandidates(timeZoneId, shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate(), shifted.getUTCHours(), shifted.getUTCMinutes());
    if (shiftedCandidates === null || shiftedCandidates.length === 0) return null;
    return Math.max(...shiftedCandidates);
}

/**
 * The consumer-facing entry point: resolve an operator-typed date + "HH:mm" against a resource's
 * own timezone and return the UTC epoch-ms instant it denotes.
 *
 * `date` is a DatePicker value, i.e. a browser-local midnight whose y/m/d IS the intended calendar
 * date; only its calendar date is read, never its instant.
 *
 * Returns null when `date` or `time` is missing or unparseable, so callers can drop the slot rather
 * than send a zero. When the zone cannot be resolved this falls back to today's exact behaviour — a
 * browser-local Date with setHours — and never returns null on account of the zone alone (D-09).
 */
export function resourceLocalDateTimeToMs(timeZoneId: string | null | undefined, date: Date | null, time: string | null): number | null {
    if (!date || !time || !time.trim()) return null;
    const parts = time.split(':').map(Number);
    if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
    const hours = parts[0];
    const minutes = parts[1];

    const resourceLocalMs = zoneWallClockToUtcMs(timeZoneId, date.getFullYear(), date.getMonth() + 1, date.getDate(), hours, minutes);
    if (resourceLocalMs !== null) return resourceLocalMs;

    const browserLocal = new Date(date);
    browserLocal.setHours(hours, minutes, 0, 0);
    return browserLocal.getTime();
}

/**
 * Render an instant as "HH:mm" in the given zone.
 * Falls back to the browser's own clock when the zone cannot be resolved (D-09), and returns an
 * empty string for a non-finite instant.
 */
export function msToResourceLocalTimeString(timeZoneId: string | null | undefined, ms: number): string {
    if (!Number.isFinite(ms)) return '';
    const parts = zoneWallClockPartsAt(timeZoneId, ms);
    if (parts) {
        return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
    }
    const browserLocal = new Date(ms);
    return `${String(browserLocal.getHours()).padStart(2, '0')}:${String(browserLocal.getMinutes()).padStart(2, '0')}`;
}

/**
 * Render an instant as a "yyyy-MM-dd" calendar date in the given zone.
 *
 * Falls back to the browser's own calendar date when the zone cannot be resolved (D-09), producing
 * byte-for-byte what formatDateToyyyMMdd produces for the same instant. Returns an empty string for
 * a non-finite instant.
 */
export function msToResourceLocalYmd(timeZoneId: string | null | undefined, ms: number): string {
    if (!Number.isFinite(ms)) return '';
    const parts = zoneWallClockPartsAt(timeZoneId, ms);
    if (parts) {
        return `${String(parts.year).padStart(4, '0')}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
    }
    return formatDateToyyyMMdd(new Date(ms));
}

/**
 * The zone's calendar date for an instant, carried in a browser-local midnight Date.
 *
 * This is deliberately the shape `form.date` and `EditingSlot.startDate` are already in — a
 * DatePicker value whose y/m/d IS the selected calendar date and whose own instant is never read —
 * so downstream code needs no new concept for a resource-local date. Do NOT read the returned
 * value's epoch-ms: it is a browser-local midnight and means nothing as an instant.
 *
 * Falls back to the browser's own calendar date when the zone cannot be resolved (D-09).
 * Returns null for a non-finite instant.
 */
export function msToResourceLocalCalendarDate(timeZoneId: string | null | undefined, ms: number): Date | null {
    if (!Number.isFinite(ms)) return null;
    const parts = zoneWallClockPartsAt(timeZoneId, ms);
    if (parts) {
        return new Date(parts.year, parts.month - 1, parts.day, 0, 0, 0, 0);
    }
    const browserLocal = new Date(ms);
    browserLocal.setHours(0, 0, 0, 0);
    return browserLocal;
}

/**
 * THE DISPLAY SHIFT. An instant's FULL wall clock in the given zone, carried in a browser-local Date.
 *
 * The direct sibling of msToResourceLocalCalendarDate above: that one carries the zone's calendar
 * date only, this one carries date AND time. It exists so FullCalendar — which has no zone of its
 * own and reads every Date on the operator's browser clock — can draw an event on the row matching
 * its resource's wall clock. A 09:00 Tokyo booking becomes "09:00 browser-local" and lands on the
 * 09:00 row by itself, so resources on different zones coexist in one grid, each event shifted
 * independently.
 *
 * THE RETURNED DATE'S OWN EPOCH VALUE IS A FICTION. Never persist it, never send it to the backend,
 * never compare it against a true instant. The only correct thing to do with it is render it or hand
 * it to a layout engine. To get back to the true instant, and you must before anything is saved, run
 * it through resourceLocalWallClockDateToMs below.
 *
 * Falls back to the identity — `new Date(ms)`, exactly today's behaviour — when the zone cannot be
 * resolved (D-09), which is what makes every calendar surface degrade to its pre-shift rendering.
 * Returns null for a non-finite instant.
 */
export function msToResourceLocalWallClockDate(timeZoneId: string | null | undefined, ms: number): Date | null {
    if (!Number.isFinite(ms)) return null;
    const parts = zoneWallClockPartsAt(timeZoneId, ms);
    if (!parts) return new Date(ms);
    // Seconds and milliseconds come from the source instant, the same idiom zoneOffsetMsAt uses:
    // zoneWallClockPartsAt deliberately carries no seconds, and re-adding them here is safe because
    // every real IANA offset is a whole number of minutes.
    const source = new Date(ms);
    return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, source.getUTCSeconds(), source.getUTCMilliseconds());
}

/**
 * THE UN-SHIFT, the exact inverse of msToResourceLocalWallClockDate: reads `date`'s browser-local
 * wall clock and re-interprets it in `timeZoneId`, returning the true UTC epoch-ms instant.
 *
 * This is what must run on anything FullCalendar hands back after a gesture — a drop, a resize, a
 * drag-select — before it is compared to a stored instant or sent to the backend. What leaves this
 * function is a TRUE instant.
 *
 * Seconds are dropped, because zoneWallClockToUtcMs resolves at minute precision. That is safe only
 * because every call site is a 15-minute-snapped drag result; do not introduce one that is not.
 *
 * The round trip un-shift(shift(ms)) returns `ms`, except for a wall clock inside a DST fold, where
 * the D-14 rule at zoneWallClockToUtcMs picks the later instant — the same rule the backend applies.
 *
 * Falls back to the identity — `date.getTime()` — when the zone cannot be resolved (D-09).
 * Returns null for a null or invalid date.
 */
export function resourceLocalWallClockDateToMs(timeZoneId: string | null | undefined, date: Date | null): number | null {
    if (!date || !Number.isFinite(date.getTime())) return null;
    const trueMs = zoneWallClockToUtcMs(timeZoneId, date.getFullYear(), date.getMonth() + 1, date.getDate(), date.getHours(), date.getMinutes());
    return trueMs !== null ? trueMs : date.getTime();
}

/**
 * The first and last instant of a calendar date, as that date is lived in the given zone (D-12).
 *
 * `date` is a DatePicker value: its y/m/d IS the selected calendar date, read with the browser-local
 * getters, and its own instant is never used. The returned pair is the zone's 00:00:00.000 and
 * 23:59:59.999 for that calendar date, in UTC epoch-ms.
 *
 * This is what puts the slot timeline's query window on the resource's day rather than the
 * operator's: rendering resource-local over a browser-local window leaves the rendered day partly
 * empty and partly bled in from the next day.
 *
 * Falls back to the browser's own day boundaries when the zone cannot be resolved (D-09).
 * Returns null when `date` is null.
 */
export function resourceLocalDayBoundsMs(timeZoneId: string | null | undefined, date: Date | null): { startMs: number; endMs: number } | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    const startMs = zoneWallClockToUtcMs(timeZoneId, year, month, day, 0, 0);
    if (startMs !== null) {
        // The last millisecond is reached as the zone's 23:59 plus 59_999 ms, not by asking for
        // 23:59:59.999 directly: zoneWallClockToUtcMs resolves at minute precision, and deriving the
        // remainder from the resolved 23:59 keeps the final millisecond on the same side of any DST
        // transition as the minute it belongs to. One millisecond past this value is therefore, by
        // construction, the first instant of the next resource-local calendar date — on a 23-hour
        // and a 25-hour local day alike.
        const lastMinuteMs = zoneWallClockToUtcMs(timeZoneId, year, month, day, 23, 59);
        if (lastMinuteMs !== null) {
            return { startMs, endMs: lastMinuteMs + 59999 };
        }
    }

    const browserStart = new Date(date);
    browserStart.setHours(0, 0, 0, 0);
    const browserEnd = new Date(date);
    browserEnd.setHours(23, 59, 59, 999);
    return { startMs: browserStart.getTime(), endMs: browserEnd.getTime() };
}

/**
 * Does the given zone read a different wall clock than the browser does, at that instant?
 *
 * ONE PREDICATE, FOUR CALL SITES (IC-1) — the hover preview card (D-18), the reservation detail
 * modal, the change-confirmation modal and the booking wizard's timezone notice (D-19). This is
 * binding: a second COPY must never be created. The moment two copies exist the surfaces can
 * disagree about whether a resource's clock is foreign, which is precisely the contradiction this
 * phase removes.
 *
 * The calendar event chip was the original fifth site and no longer needs it: the grid now draws
 * each event on its resource's own clock, so the chip's label and the row it sits on agree by
 * construction and there is nothing left for a marker to warn about.
 *
 * Returns FALSE when the zone id is null, blank or unresolvable. That is not a convenience default:
 * the D-09 degraded path owns that state, and this false is what makes IC-5's mutual exclusion hold
 * by construction — the "unrecognised time zone" warning and the "times are in the space's zone"
 * notice can never render together on one surface.
 *
 * Per D-17 the comparison is the two zones' OFFSETS at that event's own instant. Never zone-id
 * equality, which would print a redundant identical time for a Berlin resource forever; never a
 * scan across the year, which still prints two identical times for a Tunis resource in winter.
 */
export function isForeignTimezoneAt(timeZoneId: string | null | undefined, instantMs: number): boolean {
    if (!Number.isFinite(instantMs)) return false;
    const resourceOffsetMs = zoneOffsetMsAt(timeZoneId, instantMs);
    if (resourceOffsetMs === null) return false;
    // Date.getTimezoneOffset is minutes WEST of UTC, so it is negated to match zoneOffsetMsAt's
    // milliseconds east of UTC.
    const browserOffsetMs = -new Date(instantMs).getTimezoneOffset() * 60000;
    return resourceOffsetMs !== browserOffsetMs;
}

/**
 * The `{zone}` interpolation value for the D-19 notice: the IANA id, a space, and the zone's offset
 * at that instant in parentheses — for example `Europe/Rome (UTC+02:00)`.
 *
 * The id is what makes the claim verifiable against the resource's configuration; the offset is what
 * lets the operator do the arithmetic. Returns '' when the id is null, blank or unresolvable, so a
 * caller that skipped the D-09 gate renders an incomplete sentence rather than the word "null".
 */
export function formatZoneOffsetLabel(timeZoneId: string | null | undefined, instantMs: number): string {
    if (!timeZoneId || !timeZoneId.trim()) return '';
    const offsetMs = zoneOffsetMsAt(timeZoneId, instantMs);
    if (offsetMs === null) return '';
    const sign = offsetMs < 0 ? '-' : '+';
    const totalMinutes = Math.round(Math.abs(offsetMs) / 60000);
    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
    const minutes = String(totalMinutes % 60).padStart(2, '0');
    return `${timeZoneId.trim()} (UTC${sign}${hours}:${minutes})`;
}
